'use client';

import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { isBefore } from 'date-fns';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { createContext } from 'react';

import {
  getTradingCompetitionApi,
  getTradingCompetitionRankingsApi,
  postTradingCompetitionClaimRewardApi,
  postTradingCompetitionDeregisterApi,
  postTradingCompetitionRegisterApi,
} from '@flipster/apis/v1/tradingCompetition';
import {
  CompetitionStatusEnum,
  TradingCompetition,
  TradingCompetitionType,
} from '@flipster/apis/v1/tradingCompetition/types';
import { overlayBunja } from '@flipster/overlay';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { RouterScope } from '@flipster/react/providers/route';
import { AlertProps, alert } from '@flipster/ui/Alert';
import { checkAxiosError } from '@flipster/utils/axios';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getRangeStatus, timestampToDate } from '@flipster/utils/datetime';
import { gotoFlipster } from '@flipster/utils/env-helper';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { EditProfileModal } from '../profile-page/modal/edit-profile';
import { authBunja } from '../shared/auth/state';
import { webviewInfoBunja } from '../shared/webview';
import { InviteCodeModal } from './modal/invite-code-modal';

const newCompetitionDesignType: TradingCompetitionType[] = ['HOSTED_BY_INFLUENCER', 'GENERAL', 'NEW_LISTING'];
const migratedDate = new Date(Date.UTC(2025, 9, 2, 0, 0, 0, 0));

const GetTradingCompetitionQueryKey = 'getTradingCompetition';
const GetTradingCompetitionRankingsQueryKey = 'getTradingCompetitionRankings';

export const CompetitionIdContext = createContext<string>('');
const CompetitionIdScope = createScopeFromContext(CompetitionIdContext);
export const competitionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isLoggedInAtom, getMeAtom } = bunja.use(authBunja);
  const overlay = bunja.use(overlayBunja);
  const id = bunja.use(CompetitionIdScope);
  const competitionBaseAtom = atomWithQuery((get) => ({
    queryKey: [GetTradingCompetitionQueryKey, id, get(isLoggedInAtom)],
    queryFn: () => getTradingCompetitionApi(id),
    refetchOnMount: true,
    refetchInterval: 1000 * 60 * 5,
  }));
  const competitionAtom = atom((get) => get(competitionBaseAtom).data?.data);
  const competitionErrorAtom = atom((get) => get(competitionBaseAtom).error);
  async function refetchCompetition() {
    const queryClient = store.get(queryClientAtom);
    return Promise.all([
      queryClient.refetchQueries({ queryKey: [GetTradingCompetitionQueryKey, id] }),
      queryClient.refetchQueries({ queryKey: [GetTradingCompetitionRankingsQueryKey, id] }),
    ]);
  }
  const needInvitationCodeAtom = atom((get) => get(competitionAtom)?.needInvitationCode);
  const registerCompetitionMutationAtom = atomWithMutation<unknown, { invitationCode: string | undefined }, Error>(
    (get) => ({
      mutationFn: async ({ invitationCode }) => await postTradingCompetitionRegisterApi(id, { invitationCode }),
      onSuccess: async () => {
        await refetchCompetition();
        await get(getMeAtom).refetch();
        get(queryClientAtom).refetchQueries({ queryKey: ['getTradingCompetitionsListApi'], type: 'active' });
      },
    }),
  );
  async function registerCompetition(invitationCode?: string) {
    const { mutateAsync, isPending } = store.get(registerCompetitionMutationAtom);
    if (isPending) return;
    segment.track(CompetitionsEvent.CompetitionRegisterClick, { competition_id: id });
    if (store.get(needInvitationCodeAtom) && !invitationCode)
      return overlay.open(({ isOpen, onClose, unmount }) => (
        <CompetitionIdContext value={id}>
          <InviteCodeModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
        </CompetitionIdContext>
      ));
    await mutateAsync({ invitationCode });
  }
  const registerCompetitionErrorAtom = atom((get) => get(registerCompetitionMutationAtom).error);
  const registerCompetitionErrorMessageAtom = atom((get) => {
    const error = get(registerCompetitionErrorAtom);
    if (!error) return;
    return getErrorMessage(error);
  });
  function resetRegisterCompetitionError() {
    store.get(registerCompetitionMutationAtom).reset();
  }
  const isRegistrationPendingAtom = atom((get) => get(registerCompetitionMutationAtom).isPending);
  async function deregisterCompetition() {
    await postTradingCompetitionDeregisterApi(id);
    await refetchCompetition();
  }
  const registrationStatusAtom = atom((get) => {
    const competition = get(competitionAtom);
    if (!competition) return;
    return getRangeStatus(competition.registrationStartTime, competition.registrationEndTime);
  });
  const registrationMetaAtom = atom((get) => {
    const competition = get(competitionAtom);
    const registrationStatus = get(registrationStatusAtom);
    if (!competition || !registrationStatus) return;
    if (competition.competitionStatus === CompetitionStatusEnum.Finished && competition.participationTime) {
      return {
        title: 'Participation closed',
        description: 'Thank you for being part of the competition. See you in the next event.',
      };
    }
    if (competition.competitionStatus === CompetitionStatusEnum.Finished) {
      return {
        title: 'Competition ended',
        description: 'The trading competition has ended. Don’t miss the next one.',
      };
    }
    if (registrationStatus.isEnd && !competition.participationTime) {
      return {
        title: 'Registration closed',
        description: 'The registration period has ended.\nJoin us in the next competition.',
      };
    }
  });
  const overlayTextAtom = atom((get) => {
    const competition = get(competitionAtom);
    const registrationStatus = get(registrationStatusAtom);
    if (!competition || !registrationStatus) return;
    if (competition.competitionStatus === CompetitionStatusEnum.Finished && competition.participationTime) {
      return 'Thank you for being a part of Flipster Trading Competition\nStay tuned for our next event!';
    }
    if (competition.competitionStatus === CompetitionStatusEnum.Finished) {
      return 'Sorry, the trading competition has ended \nStay tuned and join us for the next trading competition!';
    }
    if (registrationStatus.isEnd && !competition.participationTime) {
      return 'The registration period is now closed.\nWe are no longer accepting participants.';
    }
  });
  // for migration purpose
  const isNewDesignTargetAtom = atom((get) => {
    const competition = get(competitionAtom);
    if (!competition) return false;
    const startTime = timestampToDate(competition.competitionStartTime);
    if (isBefore(startTime, migratedDate)) return false;
    return newCompetitionDesignType.includes(competition.competitionType);
  });
  // recurring tc
  const claimRequiredAtom = atom((get) => Boolean(get(competitionAtom)?.claimRequiredForReward));
  const claimableAtom = atom(
    (get) => get(claimRequiredAtom) && get(competitionAtom)?.competitionStatus === CompetitionStatusEnum.Ongoing,
  );
  const claimedTimeAtom = atom((get) => get(competitionAtom)?.claimedTime);
  async function claimReward() {
    const queryClient = store.get(queryClientAtom);
    try {
      const key = [GetTradingCompetitionQueryKey, id, true];
      const prev = queryClient.getQueryData<AxiosResponse<TradingCompetition>>(key);
      await postTradingCompetitionClaimRewardApi(id);
      if (prev) queryClient.setQueryData(key, { ...prev, data: { ...prev.data, claimedTime: Date.now() * 1000 } }); // optimistic update
      snackbar.success({ message: 'You are registered for the prize' });
    } catch (error) {
      alert.show({ message: getErrorMessage(error) });
    } finally {
      queryClient.invalidateQueries({ queryKey: [GetTradingCompetitionQueryKey, id] });
    }
  }
  return {
    id,
    competitionAtom,
    competitionErrorAtom,
    registerCompetition,
    deregisterCompetition,
    registerCompetitionErrorAtom,
    registerCompetitionErrorMessageAtom,
    resetRegisterCompetitionError,
    isRegistrationPendingAtom,
    registrationStatusAtom,
    registrationMetaAtom,
    isNewDesignTargetAtom,
    overlayTextAtom,
    // recurring tc
    claimRequiredAtom,
    claimableAtom,
    claimedTimeAtom,
    claimReward,
  };
});
competitionBunja.debugLabel = 'competitionBunja';

export const competitionRankingBunja = bunja(() => {
  const { id, competitionAtom } = bunja.use(competitionBunja);
  const { isLoggedInAtom } = bunja.use(authBunja);
  const competitionRankingsBaseAtom = atomWithQuery((get) => ({
    queryKey: [GetTradingCompetitionRankingsQueryKey, id, get(isLoggedInAtom)],
    queryFn: () => getTradingCompetitionRankingsApi(id),
    enabled: get(isLoggedInAtom) || get(competitionAtom)?.competitionType == 'WEEKLY_PNL',
    refetchOnMount: true,
  }));
  const competitionRankingsAtom = atom((get) => get(competitionRankingsBaseAtom).data?.data);
  const rankingsAtom = atom((get) => get(competitionRankingsAtom)?.rankings ?? []);
  const myRankingAtom = atom((get) => get(competitionRankingsAtom)?.myRanking);
  const lastRankingAtom = atom((get) => get(competitionRankingsAtom)?.lastRanking);
  const lastUpdatedAtAtom = atom((get) => get(competitionRankingsAtom)?.lastUpdatedTime);
  const isLessThanAwardListAtom = atom((get) => {
    const competitionRankings = get(competitionRankingsAtom);
    return !competitionRankings?.rankings || competitionRankings?.rankings.length < 4;
  });
  const payloadAtom = atom((get) => {
    const competition = get(competitionAtom);
    const rankings = get(competitionRankingsAtom);
    const isPnlCompetition = competition?.rankBy === 'PNL';
    return {
      competition_id: id,
      competition_type: isPnlCompetition ? 'pnl' : 'volume',
      prize_type: competition?.milestone && competition.milestone.length > 0 ? 'milestone' : 'fixed',
      current_prize: Number(competition?.currentMilestonePrizePool),
      max_prize: Number(competition?.prizePool),
      user_rank: rankings?.myRanking?.rank || 0,
      user_tc_amount: isPnlCompetition ? Number(rankings?.myRanking?.pnl) : Number(rankings?.myRanking?.volume),
    } as const;
  });
  return {
    rankingsAtom,
    myRankingAtom,
    lastRankingAtom,
    lastUpdatedAtAtom,
    isLessThanAwardListAtom,
    payloadAtom,
  };
});
competitionRankingBunja.debugLabel = 'competitionRankingBunja';

export const registerCompetitionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const router = bunja.use(RouterScope);
  const overlay = bunja.use(overlayBunja);
  const { isLoggedInAtom } = bunja.use(authBunja);
  const { registerCompetition, isRegistrationPendingAtom } = bunja.use(competitionBunja);
  const { webviewAtom } = bunja.use(webviewInfoBunja);

  async function register() {
    segment.track(CompetitionsEvent.CompetitionLandingViewClickCta);
    const isLoggedIn = store.get(isLoggedInAtom);
    try {
      if (!isLoggedIn) {
        router.push('/login');
        return;
      }
      await registerCompetition();
    } catch (err) {
      if (!checkAxiosError(err)) return;
      const errorCode = getErrorCode(err);
      const message = getErrorMessage(err);
      const alertMapper: Record<string, AlertProps> = {
        'api.tradingCompetition.missingNickname': {
          message,
          confirmText: 'Set',
          closeText: 'I’m OK for now',
          onConfirm() {
            overlay.open(() => <EditProfileModal />);
          },
        },
        'api.tradingCompetition.insufficientKYCLevel': {
          message,
          confirmText: 'Verify',
          closeText: 'I’m OK for now',
          onConfirm() {
            const path = '/user/kyc';
            if (store.get(webviewAtom)) gotoFlipster(path);
            else router.push(path);
          },
        },
        'api.tradingCompetition.exceededMaxRegistration': { message },
      };
      const alertConfig = alertMapper[errorCode];
      if (alertConfig) alert.show(alertConfig);
      else alert.error(err);
    }
  }
  return { register, isRegistrationPendingAtom };
});
registerCompetitionBunja.debugLabel = 'registerCompetitionBunja';
